import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Trash2 } from 'lucide-react';
import { useAuth } from '@/stores/authStore';
import { toast } from '@/stores/toastStore';
import { deleteUserAccount } from '@/services/api';
import Button from '@/components/common/Button';
import Input from '@/components/common/Input';
import Card from '@/components/common/Card';
import Modal from '@/components/common/Modal';

export default function DeleteAccountSection() {
  const { t } = useTranslation('profile');
  const navigate = useNavigate();
  const { logout } = useAuth();

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteAccount = async () => {
    const expectedText = t('deleteAccount.modal.placeholder')
      .replace('Type ', '')
      .replace(' to confirm', '')
      .replace('Tapez ', '')
      .replace(' pour confirmer', '')
      .replace('اكتب ', '')
      .replace(' للتأكيد', '');

    if (deleteConfirmation.trim().toUpperCase() !== expectedText.toUpperCase()) {
      toast.error(t('deleteAccount.modal.invalidConfirmation'));
      return;
    }

    setIsDeleting(true);
    try {
      await deleteUserAccount();
      toast.success(t('deleteAccount.success'));

      // Logout and redirect to home
      setTimeout(() => {
        logout();
        navigate('/');
      }, 1500);
    } catch (error: any) {
      console.error('Failed to delete account:', error);
      toast.error(error.response?.data?.error || t('deleteAccount.error'));
    } finally {
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
      setDeleteConfirmation('');
    }
  };

  return (
    <>
      {/* Delete Account Section - Danger Zone */}
      <div className="animate-slide-up delay-200">
        <Card className="bg-white border-2 border-red-200 rounded-xl p-8 shadow-lg">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('deleteAccount.title')}</h2>
              <p className="text-gray-600 mb-6">{t('deleteAccount.description')}</p>
              <Button
                variant="danger"
                onClick={() => setIsDeleteModalOpen(true)}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                <Trash2 className="w-5 h-5 mr-2" />
                {t('deleteAccount.button')}
              </Button>
            </div>
          </div>
        </Card>
      </div>

      {/* Delete Account Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setDeleteConfirmation('');
        }}
        title={t('deleteAccount.modal.title')}
        size="lg"
      >
        <div className="space-y-6">
          {/* Warning Banner */}
          <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-red-900">{t('deleteAccount.modal.warning')}</p>
              <p className="text-red-700 text-sm mt-1">{t('deleteAccount.modal.confirmationText')}</p>
            </div>
          </div>

          {/* Consequences List */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">{t('deleteAccount.modal.consequences.title')}</h3>
            <ul className="space-y-2">
              <li className="flex items-start gap-2 text-gray-700">
                <span className="text-red-600 mt-1">•</span>
                <span>{t('deleteAccount.modal.consequences.item1')}</span>
              </li>
              <li className="flex items-start gap-2 text-gray-700">
                <span className="text-red-600 mt-1">•</span>
                <span>{t('deleteAccount.modal.consequences.item2')}</span>
              </li>
              <li className="flex items-start gap-2 text-gray-700">
                <span className="text-red-600 mt-1">•</span>
                <span>{t('deleteAccount.modal.consequences.item3')}</span>
              </li>
              <li className="flex items-start gap-2 text-gray-700">
                <span className="text-red-600 mt-1">•</span>
                <span>{t('deleteAccount.modal.consequences.item4')}</span>
              </li>
              <li className="flex items-start gap-2 text-gray-700">
                <span className="text-red-600 mt-1">•</span>
                <span>{t('deleteAccount.modal.consequences.item5')}</span>
              </li>
            </ul>
          </div>

          {/* Confirmation Input */}
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              {t('deleteAccount.modal.typeConfirmation')}
            </label>
            <Input
              type="text"
              value={deleteConfirmation}
              onChange={(e) => setDeleteConfirmation(e.target.value)}
              placeholder={t('deleteAccount.modal.placeholder')}
              className="font-mono"
              disabled={isDeleting}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 justify-end pt-4 border-t border-gray-200">
            <Button
              variant="glass"
              onClick={() => {
                setIsDeleteModalOpen(false);
                setDeleteConfirmation('');
              }}
              disabled={isDeleting}
            >
              {t('deleteAccount.modal.cancelButton')}
            </Button>
            <Button
              variant="danger"
              onClick={handleDeleteAccount}
              isLoading={isDeleting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              <Trash2 className="w-5 h-5 mr-2" />
              {t('deleteAccount.modal.confirmButton')}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
